import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useAuth } from "./use-auth";
import { Task } from "@shared/schema";
import { useToast } from "./use-toast";

type OnboardingContextType = {
  isNewUser: boolean;
  hasCompletedTasks: boolean;
  isLoading: boolean;
  shouldShowOnboarding: boolean;
  isOnboardingCompleted: boolean;
  markOnboardingComplete: () => void;
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isUserLoading } = useAuth();
  const { toast } = useToast();
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState<boolean>(false);
  
  // Fetch user tasks to determine if they have completed any
  const {
    data: tasks,
    isLoading: isTasksLoading,
  } = useQuery<Task[], Error>({
    queryKey: ["/api/tasks"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!user, // Only fetch tasks if user is logged in
  });
  
  // Mutation to mark onboarding as complete on the server
  const completeOnboardingMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/onboarding/complete");
      return await response.json();
    },
    onSuccess: () => {
      // Invalidate user data cache to refresh the isFirstLogin flag
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      // Invalidate tasks to refresh completed status
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
    onError: (error: Error) => {
      console.error("Error marking onboarding as complete:", error);
      toast({
        title: "Error",
        description: "There was a problem completing your onboarding. Please try again.",
        variant: "destructive",
      });
    }
  });
  
  // Determine if user is new based on isFirstLogin flag or lack of completed tasks
  const isNewUser = !!user?.isFirstLogin;
  
  // Check if user has completed any tasks
  const hasCompletedTasks = tasks ? tasks.some(task => task.status === "completed") : false;
  
  // Combine the loading states
  const isLoading = isUserLoading || isTasksLoading;
  
  // Determine if we should show onboarding
  // Show onboarding if user is new AND has no completed tasks AND hasn't explicitly completed onboarding
  const shouldShowOnboarding = !isLoading && !!user && isNewUser && !hasCompletedTasks && !isOnboardingCompleted;
  
  // Function to mark onboarding as complete in this session and on the server
  const markOnboardingComplete = () => {
    setIsOnboardingCompleted(true);
    
    // Make an API call to update the user's isFirstLogin flag and complete any verification tasks
    if (user) {
      completeOnboardingMutation.mutate();
    }
  };
  
  return (
    <OnboardingContext.Provider
      value={{
        isNewUser,
        hasCompletedTasks,
        isLoading,
        shouldShowOnboarding,
        isOnboardingCompleted,
        markOnboardingComplete,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}